package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

// Shared, user-selectable time-zone picker for CMC applications. The IANA zone id is the single
// source of truth; the selection is persisted to localStorage so it sticks across pages and
// reloads. Browser (Intl) interop and DST-correct conversion helpers live in TimeZone.kt.

/**
 * Stylesheet for the time-zone picker and surrounding chrome. Attach once where used via
 * `Style(TimeZoneStyles)`.
 */
object TimeZoneStyles : StyleSheet() {
    /** Left-aligned standalone header row, used above list-style views. */
    val tzHeaderRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexStart)
        alignItems(AlignItems.Center)
        gap(6.px)
        margin(8.px, 0.px)
        fontWeight(700)
    }

    /**
     * Wrapper around the visible label + the overlaid native `<select>`. Provides the positioning
     * context so the transparent select can be stretched over the label.
     */
    val tzPickerRoot by style {
        position(Position.Relative)
        display(DisplayStyle.InlineBlock)
    }

    /**
     * Collapsed visible display ("PDT ▾"). Painted underneath the transparent native select; the
     * select captures the actual interaction so this is purely presentational.
     */
    val tzPickerTrigger by style {
        cursor("pointer")
        padding(2.px, 8.px)
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(4.px)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        color(SXMUI.textColor.value())
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
        whiteSpace("nowrap")
        fontWeight(700)
        fontSize(0.9.cssRem)
    }

    /**
     * The real, interactive `<select>` overlaid on top of [tzPickerTrigger]. It is stretched to
     * cover the label and made fully transparent so the compact abbreviation below shows through,
     * while the element itself still owns focus, keyboard navigation, screen-reader semantics, and
     * the OS-native option dropdown. `appearance: none` and a transparent color hide the browser's
     * own chrome and the (longer) selected-option text.
     */
    val tzNativeSelect by style {
        position(Position.Absolute)
        top(0.px)
        left(0.px)
        width(100.percent)
        height(100.percent)
        margin(0.px)
        padding(0.px)
        opacity(0)
        cursor("pointer")
        property("border", "none")
        property("background", "transparent")
        property("appearance", "none")
        property("-webkit-appearance", "none")
    }
}

/**
 * Curated IANA zones surfaced in the picker. Driven by IANA IDs so the displayed
 * abbreviation auto-flips for DST (EST→EDT, etc).
 */
private val commonTimeZoneIds: List<String> = listOf(
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
    "UTC",
    "Europe/London",
    "Europe/Paris",
    "Asia/Kolkata",
    "Asia/Tokyo",
    "Australia/Sydney",
)

private const val TZ_STORAGE_KEY = "cmc.timeZoneId"

/**
 * The single, app-wide chosen time zone (IANA id). Module-level snapshot state so that a change
 * made by any picker recomposes every reader across all views — one source of truth, no threading
 * through composable signatures. Initialized from `localStorage` (so the selection sticks across
 * pages and reloads) or the browser's local zone when nothing is stored.
 *
 * Kotlin/JS is single-threaded and there is one document, so a top-level [mutableStateOf] is a
 * safe global holder; reading it inside composition registers the reader the same as any other
 * snapshot state.
 */
private val timeZoneIdState: MutableState<String> = mutableStateOf(
    runCatching { localStorage.getItem(TZ_STORAGE_KEY) }.getOrNull()
        ?.takeIf { it.isNotBlank() } ?: localTimeZoneId()
)

/** Reactive read of the app-wide chosen IANA zone. Safe to call from any composable. */
fun currentTimeZoneId(): String = timeZoneIdState.value

/** Set and persist the app-wide zone; recomposes every reader of [currentTimeZoneId]. */
fun selectTimeZoneId(id: String) {
    timeZoneIdState.value = id
    runCatching { localStorage.setItem(TZ_STORAGE_KEY, id) }
}

/**
 * The shared zone state, for the few call sites that want a [MutableState] directly. Returns the
 * one global holder; writes still persist when routed through [selectTimeZoneId] (preferred) or
 * the self-persisting pickers below.
 */
@Composable
fun rememberTimeZoneId(): MutableState<String> = timeZoneIdState

/**
 * Picker for the time zone the views render in. The IANA id is the source of truth;
 * callers carry it as a `String`. Display sites use [Instant.toLocalDateTimeIn]
 * for DST-correct conversion.
 *
 * Backed by a real, but visually hidden, native `<select>`: this gives full keyboard navigation,
 * screen-reader semantics, and the OS-native option list (including mobile pickers) for free. The
 * select is stretched over a compact visible label ("PDT ▾") and made transparent, so the collapsed
 * display stays minimal (just the abbreviation) while the open dropdown shows each option's
 * abbreviation + GMT offset. All interaction — click, focus, arrow keys — lands on the select.
 */
@Composable
fun TimeZoneSelector(
    selectedId: String,
    onSelect: (String) -> Unit,
) {
    // Build the option set once per selected zone — Intl calls are not free.
    // Local zone goes first so it's the most accessible; then the curated list with
    // local de-duped; then the currently selected zone if it isn't already present.
    val options = remember(selectedId) {
        val localId = localTimeZoneId()
        val ordered = buildList {
            add(localId)
            commonTimeZoneIds.forEach { if (it != localId) add(it) }
            if (selectedId !in this) add(selectedId)
        }
        ordered.map { id ->
            val abbr = timeZoneAbbreviation(id)
            val offset = timeZoneOffset(id)
            // Some zones (e.g. Sydney in en-US) report the offset as the short name. Avoid duplication.
            val label = if (abbr == offset) abbr else "$abbr  $offset"
            id to label
        }
    }
    val selectedAbbr = remember(selectedId) { timeZoneAbbreviation(selectedId) }

    Div({ classes(TimeZoneStyles.tzPickerRoot) }) {
        // Visible, presentational-only label painted beneath the transparent select.
        Div({ classes(TimeZoneStyles.tzPickerTrigger) }) {
            Text(selectedAbbr)
            Text("▾")
        }

        // The real control. Transparent and overlaid; the option value is the IANA id.
        Select({
            classes(TimeZoneStyles.tzNativeSelect)
            // Native selects don't take an aria-label via a title attr, so give it one for SR users.
            attr("aria-label", "Time zone")
            onChange { event ->
                event.value?.let { id -> if (id != selectedId) onSelect(id) }
            }
        }) {
            options.forEach { (id, label) ->
                Option(id, {
                    if (id == selectedId) selected()
                }) { Text(label) }
            }
        }
    }
}

/**
 * Self-contained picker bound to the app-wide shared zone: reads [currentTimeZoneId] and persists
 * via [selectTimeZoneId]. Use this everywhere — call sites need no state of their own.
 */
@Composable
fun TimeZoneSelector() {
    TimeZoneSelector(currentTimeZoneId()) { selectTimeZoneId(it) }
}

/**
 * The global timezone selector for the application header. Bound to the app-wide shared zone, but
 * unlike [TimeZoneSelector], selecting a new zone triggers a full page reload rather than an
 * in-place recomposition.
 *
 * A hard reload is intentional: the chosen zone must reach every view and every embedded iframe
 * app (which read it from their `src` query param / the `CMCContainer`) consistently, and those
 * consumers are not all reactive to the shared snapshot state. The new zone is persisted to
 * localStorage *before* reloading, so [timeZoneIdState] re-initializes from it on the next load and
 * the selection survives the reload. Re-selecting the current zone is a no-op (no reload).
 */
@Composable
fun HeaderTimeZoneSelector() {
    TimeZoneSelector(currentTimeZoneId()) { id ->
        if (id != currentTimeZoneId()) {
            selectTimeZoneId(id)
            window.location.reload()
        }
    }
}

/**
 * The "Time [TZ▾]" pair, no surrounding layout. Drop in directly next to other
 * controls in a row that already has its own flex layout.
 *
 * No-arg form binds to the app-wide shared zone; the explicit form is kept for the few callers
 * that carry their own id (writes are routed through [selectTimeZoneId] so they still persist).
 */
@Composable
fun timeZoneInlineControl() {
    Text("Time")
    TimeZoneSelector()
}

@Composable
fun timeZoneInlineControl(zoneId: String, onSelect: (String) -> Unit) {
    Text("Time")
    TimeZoneSelector(zoneId, onSelect)
}
